import React, { useState, useEffect, useRef } from 'react';
import { Button, Input, Dropdown, Menu, Tooltip, message, Modal, Spin } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, DeleteOutlined, CommentOutlined, DownOutlined, UpOutlined, EditOutlined, MoreOutlined } from '@ant-design/icons';
import '../../styles/CommentSection.css';
import LearnerCourseService from '../../services/LearnerCourseService';
import QuillUpload from './QuillUpload';
import DOMPurify from 'dompurify';
import { ALargeSmall } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Role } from '../../constants/Role';

// Helper: format time ago
function timeAgo(date) {
  const now = new Date();
  const diff = Math.floor((now - new Date(date)) / 1000);
  if (diff < 0) return 'just now'; 
  if (diff < 60) return `${diff} second${diff === 1 ? '' : 's'} ago`;
  if (diff < 3600) {
    const mins = Math.floor(diff / 60);
    return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  }
  if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  }
  if (diff < 2592000) {
    const days = Math.floor(diff / 86400);
    return `${days} day${days === 1 ? '' : 's'} ago`;
  }
  if (diff < 31536000) {
    const months = Math.floor(diff / 2592000);
    return `${months} month${months === 1 ? '' : 's'} ago`;
  }
  const years = Math.floor(diff / 31536000);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

const sortOptions = [
  { label: 'Highest votes', value: 'votes' },
  { label: 'Newest', value: 'newest' },
  { label: "Your comments first", value: "userFirst" },
];

const MAX_LENGTH = 500;

// check character
function getPlainTextLength(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.innerText.trim().length;
}

// check img 
function hasImage(html) {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.querySelector('img') !== null;
}

export default function CommentSection({ lessonId }) {
  const location = useLocation();
  const { hasRole } = useAuth();
  
  // Detect mentor role from query param or user context
  const isMentor = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('role') === 'mentor' || hasRole(Role.STAFF_COURSE_MENTOR);
  }, [location.search, hasRole]);
  const [comments, setComments] = useState([]);
  const [sortBy, setSortBy] = useState('userFirst');
  const [commentInput, setCommentInput] = useState('');
  const [replyInputs, setReplyInputs] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [posting, setPosting] = useState(false);
  const [openReplies, setOpenReplies] = useState({});
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [editingReplyId, setEditingReplyId] = useState(null);
  const [editingReplyContent, setEditingReplyContent] = useState('');
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [lazyThreshold] = useState(30); // Ngưỡng lazy load
  const [isLazyLimit, setIsLazyLimit] = useState(false); // Đã đạt ngưỡng lazy chưa
  const [showScrollTop, setShowScrollTop] = useState(false);
  const listRef = useRef();
  const hasUserScrolled = useRef(false);
  const lastScrollY = useRef(0);

  // Map BE sortBy <-> FE sortBy
  const beSortMap = { votes: 'votingIndex', newest: 'newest', userFirst: 'userFirst' };
  const feSortMap = { votingIndex: 'votes', newest: 'newest', userFirst: 'userFirst' };

  // Fetch first page or when sort/lessonId changes
  useEffect(() => {
    if (!lessonId) return;
    setLoading(true);
    setPage(1);
    setIsLazyLimit(false);
    Promise.all([
      LearnerCourseService.getLessonComments(lessonId, beSortMap[sortBy] || 'votingIndex', 1, limit),
      LearnerCourseService.getLessonCommentCount(lessonId)
    ])
      .then(([data, totalCountApi]) => {
        const feComments = (data.comments || []).map(c => {
          // Debug: log để kiểm tra cấu trúc data
          console.log('Comment author:', c.author);
          console.log('Author roles:', c.author?.roles);
          console.log('Author role:', c.author?.role);
          console.log('Author isMentor:', c.author?.isMentor);
          
          // Kiểm tra xem author có phải là current user không
          const isCurrentUser = c.isOwner;
          const isMentor = isCurrentUser ? hasRole(Role.STAFF_COURSE_MENTOR) : 
                          c.author?.roles?.includes('STAFF_COURSE_MENTOR') || 
                          c.author?.roles?.includes('MENTOR') ||
                          c.author?.role === 'MENTOR' ||
                          c.author?.isMentor === true ||
                          c.author?.role === 'STAFF_COURSE_MENTOR' ||
                          c.author?.email?.toLowerCase().includes('mentor') ||
                          c.author?.firstName?.toLowerCase().includes('mentor') ||
                          c.author?.lastName?.toLowerCase().includes('mentor');
          
          console.log('Is mentor calculated:', isMentor);
          
          return {
            id: c._id,
            userId: c.author?._id || '',
            userName: c.isOwner ? 'Me' : (c.author ? `${c.author.firstName || ''} ${c.author.lastName || ''}`.trim() : 'Unknown'),
            isMentor: isMentor,
            content: c.comment,
            createdAt: c.createdAt,
            upvotes: c.upvotes || 0,
            downvotes: c.downvotes || 0,
            votingIndex: c.votingIndex || 0,
            userVote: c.userVote === 'upvote' ? 'up' : (c.userVote === 'downvote' ? 'down' : null),
            isOwner: c.isOwner,
            replies: (c.replies || []).map(r => {
              const isCurrentUserReply = r.isOwner;
              const replyIsMentor = isCurrentUserReply ? hasRole(Role.STAFF_COURSE_MENTOR) :
                                   r.author?.roles?.includes('STAFF_COURSE_MENTOR') || 
                                   r.author?.roles?.includes('MENTOR') ||
                                   r.author?.role === 'MENTOR' ||
                                   r.author?.isMentor === true ||
                                   r.author?.role === 'STAFF_COURSE_MENTOR' ||
                                   r.author?.email?.toLowerCase().includes('mentor') ||
                                   r.author?.firstName?.toLowerCase().includes('mentor') ||
                                   r.author?.lastName?.toLowerCase().includes('mentor');
              
              return {
                id: r._id,
                userId: r.author?._id || '',
                userName: r.isOwner ? 'Me' : (r.author ? `${r.author.firstName || ''} ${r.author.lastName || ''}`.trim() : 'Unknown'),
                isMentor: replyIsMentor,
                content: r.comment,
                createdAt: r.createdAt,
                isOwner: r.isOwner,
              };
            }),
            repliesCount: c.repliesCount || (c.replies ? c.replies.length : 0),
          };
        });
        setComments(feComments);
        setTotalCount(totalCountApi);
        setHasNextPage(data.pagination?.hasNextPage ?? false);
      })
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  }, [lessonId, sortBy, limit]);

  useEffect(() => {
    const onScroll = () => {
      if (window.scrollY > 2000) {
        hasUserScrolled.current = true;
      }
    };
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    hasUserScrolled.current = false;
  }, [lessonId, sortBy]);

  // Lazy load more when scroll to bottom and < threshold (theo dõi scroll toàn trang, chỉ load khi scroll >= 80% và user đã scroll đủ xa)
  useEffect(() => {
    if (!hasNextPage) return;
    const handleWindowScroll = () => {
      if (!hasUserScrolled.current) return;
      const scrollY = window.scrollY || window.pageYOffset;
      const windowHeight = window.innerHeight;
      const docHeight = document.documentElement.offsetHeight;
      const scrollPercent = (scrollY + windowHeight) / docHeight;
      // Chỉ trigger khi user kéo xuống
      if (scrollPercent >= 0.8 && !loadingMore && scrollY > lastScrollY.current) {
        loadMoreComments();
      }
      lastScrollY.current = scrollY;
    };
    window.addEventListener('scroll', handleWindowScroll);
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, [comments, loadingMore, hasNextPage, lessonId, sortBy]);

  const loadMoreComments = async () => {
    if (loadingMore || !hasNextPage) return;
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const data = await LearnerCourseService.getLessonComments(lessonId, beSortMap[sortBy] || 'votingIndex', nextPage, limit);
      const feComments = (data.comments || []).map(c => {
        const isCurrentUser = c.isOwner;
        const isMentor = isCurrentUser ? hasRole(Role.STAFF_COURSE_MENTOR) : 
                        c.author?.roles?.includes('STAFF_COURSE_MENTOR') || 
                        c.author?.roles?.includes('MENTOR') ||
                        c.author?.role === 'MENTOR' ||
                        c.author?.isMentor === true ||
                        c.author?.role === 'STAFF_COURSE_MENTOR' ||
                        c.author?.email?.toLowerCase().includes('mentor') ||
                        c.author?.firstName?.toLowerCase().includes('mentor') ||
                        c.author?.lastName?.toLowerCase().includes('mentor');
        
        return {
          id: c._id,
          userId: c.author?._id || '',
          userName: c.isOwner ? 'Me' : (c.author ? `${c.author.firstName || ''} ${c.author.lastName || ''}`.trim() : 'Unknown'),
          isMentor: isMentor,
          content: c.comment,
          createdAt: c.createdAt,
          upvotes: c.upvotes || 0,
          downvotes: c.downvotes || 0,
          votingIndex: c.votingIndex || 0,
          userVote: c.userVote === 'upvote' ? 'up' : (c.userVote === 'downvote' ? 'down' : null),
          isOwner: c.isOwner,
          replies: (c.replies || []).map(r => {
            const isCurrentUserReply = r.isOwner;
            const replyIsMentor = isCurrentUserReply ? hasRole(Role.STAFF_COURSE_MENTOR) :
                                 r.author?.roles?.includes('STAFF_COURSE_MENTOR') || 
                                 r.author?.roles?.includes('MENTOR') ||
                                 r.author?.role === 'MENTOR' ||
                                 r.author?.isMentor === true ||
                                 r.author?.role === 'STAFF_COURSE_MENTOR' ||
                                 r.author?.email?.toLowerCase().includes('mentor') ||
                                 r.author?.firstName?.toLowerCase().includes('mentor') ||
                                 r.author?.lastName?.toLowerCase().includes('mentor');
            
            return {
              id: r._id,
              userId: r.author?._id || '',
              userName: r.isOwner ? 'Me' : (r.author ? `${r.author.firstName || ''} ${r.author.lastName || ''}`.trim() : 'Unknown'),
              isMentor: replyIsMentor,
              content: r.comment,
              createdAt: r.createdAt,
              isOwner: r.isOwner,
            };
          }),
          repliesCount: c.repliesCount || (c.replies ? c.replies.length : 0),
        };
      });
      setComments(prev => [...prev, ...feComments]);
      setPage(nextPage);
      setHasNextPage(data.pagination?.hasNextPage ?? false);
      setTotalCount(data.pagination?.totalCount || 0);
      if (comments.length + feComments.length >= lazyThreshold) setIsLazyLimit(true);
    } catch {
      // ignore
    } finally {
      setLoadingMore(false);
    }
  };

  // Khi bấm Show more sau khi đạt ngưỡng lazy
  const handleShowMore = () => {
    setIsLazyLimit(false);
    loadMoreComments();
  };

  // Hiện/ẩn replies (mở khi reply hoặc show replies)
  const handleToggleReplies = (commentId) => {
    setOpenReplies(prev => ({ ...prev, [commentId]: !prev[commentId] }));
    // Nếu đang reply vào comment này mà đóng replies thì cũng đóng khung reply
    if (openReplies[commentId]) setReplyingTo(null);
  };

  // Khi bấm Reply thì luôn mở replies
  const handleReplyClick = (commentId) => {
    setReplyingTo(replyingTo === commentId ? null : commentId);
    setOpenReplies(prev => ({ ...prev, [commentId]: true }));
  };

  // Handle vote
  const handleVote = async (commentId, type) => {
    // type: 'up' | 'down' => BE: 'upvote' | 'downvote'
    const voteType = type === 'up' ? 'upvote' : 'downvote';
    try {
      const data = await LearnerCourseService.voteLessonComment(commentId, voteType);
      // Cập nhật lại comment trong state theo data trả về từ BE
      setComments(prev => prev.map(c =>
        c.id === commentId
          ? {
              ...c,
              upvotes: data.comment.upvotes,
              downvotes: data.comment.downvotes,
              votingIndex: data.comment.votingIndex,
              userVote: data.comment.userVote === 'upvote' ? 'up' : (data.comment.userVote === 'downvote' ? 'down' : null),
            }
          : c
      ));
    } catch (e) {
      message.error('Failed to vote');
    }
  };

  // Handle post comment
  const handlePostComment = async () => {
    const trimmed = commentInput.trim();
    const sanitized = DOMPurify.sanitize(trimmed);
    if (!sanitized) return;
    setPosting(true);
    try {
      const res = await LearnerCourseService.postLessonComment(lessonId, sanitized, null);
      setCommentInput('');
      // Tạo object comment mới giống mapping ở fetch
      const c = res.comment;
      const newComment = {
        id: c._id,
        userId: c.author?._id || '',
        userName: 'Me',
        isMentor: hasRole(Role.STAFF_COURSE_MENTOR),
        content: c.comment,
        createdAt: c.createdAt,
        upvotes: 0,
        downvotes: 0,
        votingIndex: 0,
        userVote: null,
        isOwner: true,
        replies: [],
        repliesCount: 0,
        justPosted: true,
      };
      setComments(prev => [newComment, ...prev]);
      setTotalCount(prev => prev + 1);
      // Không reloadComments ở đây
    } catch (e) {
      message.error('Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  // Helper: reload comments
  const reloadComments = async () => {
    setLoading(true);
    try {
      const [data, totalCountApi] = await Promise.all([
        LearnerCourseService.getLessonComments(lessonId, beSortMap[sortBy] || 'votingIndex'),
        LearnerCourseService.getLessonCommentCount(lessonId)
      ]);
      const feComments = (data.comments || []).map(c => {
        const isCurrentUser = c.isOwner;
        const isMentor = isCurrentUser ? hasRole(Role.STAFF_COURSE_MENTOR) : 
                        c.author?.roles?.includes('STAFF_COURSE_MENTOR') || 
                        c.author?.roles?.includes('MENTOR') ||
                        c.author?.role === 'MENTOR' ||
                        c.author?.isMentor === true ||
                        c.author?.role === 'STAFF_COURSE_MENTOR' ||
                        c.author?.email?.toLowerCase().includes('mentor') ||
                        c.author?.firstName?.toLowerCase().includes('mentor') ||
                        c.author?.lastName?.toLowerCase().includes('mentor');
        
        return {
          id: c._id,
          userId: c.author?._id || '',
          userName: c.isOwner ? 'Me' : (c.author ? `${c.author.firstName || ''} ${c.author.lastName || ''}`.trim() : 'Unknown'),
          isMentor: isMentor,
          content: c.comment,
          createdAt: c.createdAt,
          upvotes: c.upvotes || 0,
          downvotes: c.downvotes || 0,
          votingIndex: c.votingIndex || 0,
          userVote: c.userVote === 'upvote' ? 'up' : (c.userVote === 'downvote' ? 'down' : null),
          isOwner: c.isOwner,
          replies: (c.replies || []).map(r => {
            const isCurrentUserReply = r.isOwner;
            const replyIsMentor = isCurrentUserReply ? hasRole(Role.STAFF_COURSE_MENTOR) :
                                 r.author?.roles?.includes('STAFF_COURSE_MENTOR') || 
                                 r.author?.roles?.includes('MENTOR') ||
                                 r.author?.role === 'MENTOR' ||
                                 r.author?.isMentor === true ||
                                 r.author?.role === 'STAFF_COURSE_MENTOR' ||
                                 r.author?.email?.toLowerCase().includes('mentor') ||
                                 r.author?.firstName?.toLowerCase().includes('mentor') ||
                                 r.author?.lastName?.toLowerCase().includes('mentor');
            
            return {
              id: r._id,
              userId: r.author?._id || '',
              userName: r.isOwner ? 'Me' : (r.author ? `${r.author.firstName || ''} ${r.author.lastName || ''}`.trim() : 'Unknown'),
              isMentor: replyIsMentor,
              content: r.comment,
              createdAt: r.createdAt,
              isOwner: r.isOwner,
            };
          }),
          repliesCount: c.repliesCount || (c.replies ? c.replies.length : 0),
        };
      });
      setComments(feComments);
      setTotalCount(totalCountApi);
      setHasNextPage(data.pagination?.hasNextPage ?? false);
    } catch {
      setComments([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle post reply
  const handlePostReply = async (commentId) => {
    const replyText = (replyInputs[commentId] || '').trim();
    const sanitized = DOMPurify.sanitize(replyText);
    if (!sanitized) return;
    setPosting(true);
    try {
      await LearnerCourseService.postLessonComment(lessonId, sanitized, commentId);
      setReplyInputs(inputs => ({ ...inputs, [commentId]: '' }));
      setReplyingTo(null);
      await reloadComments();
    } catch (e) {
      message.error('Failed to post reply');
    } finally {
      setPosting(false);
    }
  };

  // Handle delete comment/reply
  const handleDelete = (commentId, isReply, replyId) => {
    if (!isReply) {
      setComments(prev => prev.filter(c => c.id !== commentId));
    } else {
      setComments(prev => prev.map(c =>
        c.id === commentId ? {
          ...c,
          replies: c.replies.filter(r => r.id !== replyId)
        } : c
      ));
    }
  };

  // Callback cho edit/delete 
  const handleEditComment = (comment) => {
    setEditingCommentId(comment.id);
    setEditingContent(comment.content);
  };
  const handleDeleteComment = (comment) => {
    Modal.confirm({
      title: 'Confirm delete comment',
      content: 'Are you sure you want to delete this comment? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await LearnerCourseService.deleteLessonComment(comment.id);
          setComments(prev => prev.filter(c => c.id !== comment.id));
          message.success('Comment deleted!');
        } catch (e) {
          message.error('Failed to delete comment');
        }
      },
    });
  };
  const handleEditReply = (reply, parentComment) => {
    setEditingReplyId(reply.id);
    setEditingReplyContent(reply.content);
  };
  const handleDeleteReply = (reply, parentComment) => {
    Modal.confirm({
      title: 'Confirm delete reply',
      content: 'Are you sure you want to delete this reply? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          await LearnerCourseService.deleteLessonComment(reply.id);
          setComments(prev => prev.map(c =>
            c.id === parentComment.id
              ? { ...c, replies: c.replies.filter(r => r.id !== reply.id), repliesCount: c.repliesCount - 1 }
              : c
          ));
          message.success('Reply deleted!');
        } catch (e) {
          message.error('Failed to delete reply');
        }
      },
    });
  };
  const handleCancelEditComment = () => {
    setEditingCommentId(null);
    setEditingContent('');
  };
  const handleCancelEditReply = () => {
    setEditingReplyId(null);
    setEditingReplyContent('');
  };
  const handleSaveEditComment = async (comment) => {
    const trimmed = editingContent.trim();
    const sanitized = DOMPurify.sanitize(trimmed);
    if (!sanitized) return;
    try {
      const data = await LearnerCourseService.updateLessonComment(comment.id, sanitized);
      setComments(prev => prev.map(c =>
        c.id === comment.id
          ? { ...c, content: data.comment.comment, updatedAt: data.comment.updatedAt }
          : c
      ));
      setEditingCommentId(null);
      setEditingContent('');
      message.success('Comment updated!');
    } catch (e) {
      message.error('Failed to update comment');
    }
  };
  const handleSaveEditReply = async (reply, parentComment) => {
    const trimmed = editingReplyContent.trim();
    const sanitized = DOMPurify.sanitize(trimmed);
    if (!sanitized) return;
    try {
      const data = await LearnerCourseService.updateLessonComment(reply.id, sanitized);
      setComments(prev => prev.map(c =>
        c.id === parentComment.id
          ? { ...c, replies: c.replies.map(r => r.id === reply.id ? { ...r, content: data.comment.comment, updatedAt: data.comment.updatedAt } : r) }
          : c
      ));
      setEditingReplyId(null);
      setEditingReplyContent('');
      message.success('Reply updated!');
    } catch (e) {
      message.error('Failed to update reply');
    }
  };

  // Theo dõi scroll toàn trang để hiện nút scroll to top
  useEffect(() => {
    const handleWindowScroll = () => {
      setShowScrollTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleWindowScroll);
    return () => window.removeEventListener('scroll', handleWindowScroll);
  }, []);

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="comment-section">
      <div className="comment-form">
         {/* post cmt */}
        <QuillUpload
          value={commentInput}
          onChange={val => setCommentInput(val)}
          placeholder="What did you think about this lesson? Share your insights..."
          readOnly={posting || loading}
          className="comment-quill-editor"
        />
        {(() => {
          const plainLength = getPlainTextLength(commentInput);
          const isOverLimit = plainLength > MAX_LENGTH;
          const canPost = !posting && !loading && !isOverLimit && (plainLength > 0 || hasImage(commentInput));
          return (
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 2 }}>
              <span style={{ color: isOverLimit ? 'red' : '#bbb', fontSize: 13, marginRight: 12 }}>{plainLength}/{MAX_LENGTH}</span>
              <Button type="primary" onClick={handlePostComment} style={{ marginTop: 8, marginBottom: 8, minWidth: 140, borderRadius:20 }} loading={posting} disabled={!canPost}>
                Post Comment
              </Button>
            </div>
          );
        })()}
      </div>
      <div className="comment-sort-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <span style={{ marginRight: 8 }}>Sort by:</span>
          <Dropdown
            overlay={<Menu selectedKeys={[sortBy]} onClick={({ key }) => setSortBy(key)}>
              {sortOptions.map(opt => <Menu.Item key={opt.value}>{opt.label}</Menu.Item>)}
            </Menu>}
            trigger={['click']}
          >
            <Button>{sortOptions.find(opt => opt.value === sortBy)?.label}</Button>
          </Dropdown>
        </div>
        {/* <span>{totalCommentCount} comments</span> */}
        <span>{`Total ${totalCount} comments`}</span>
      </div>
      <div className="comment-list" ref={listRef}>
        {loading ? <div style={{textAlign:'center',padding:32}}><Spin /></div> :
          comments.length === 0 ? (
            <div style={{ color: '#888', marginTop: 32, textAlign: 'center' }}>No comments yet. Be the first to comment!</div>
          ) : (
            <>
              {comments.map(comment => {
                const isRepliesOpen = openReplies[comment.id] || replyingTo === comment.id;
                const voteIndex = comment.upvotes - comment.downvotes;
                const voteBoxStyle = voteIndex < 0
                  ? { color: '#f5222d', fontWeight: 700, fontSize: 18, borderRadius: 8, padding: '2px 10px', marginBottom: 2 }
                  : {  color: '#34c759', fontWeight: 700, fontSize: 18, borderRadius: 8, padding: '2px 10px', marginBottom: 2 };
                return (
                  <div key={comment.id} className="comment-item" style={{display:'flex',alignItems:'flex-start',background:'#fff',borderRadius:12,boxShadow:'0 2px 8px #e0e0e0',padding:'18px 18px 8px 18px',marginBottom:18,position:'relative'}}>
                    {/* Vote box */}
                    <div style={{minWidth:36,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'flex-start',marginRight:16,marginBottom:12}}>
                      <Tooltip title={comment.isOwner ? 'You cannot vote for your own comment' : ''}>
                        <Button
                          type="text"
                          icon={<ArrowUpOutlined style={{color: comment.isOwner ? '#bbb' : (comment.userVote==='up' ? '#34c759' : '#222'), fontSize: 15}} />}
                          onClick={() => handleVote(comment.id, 'up')}
                          disabled={comment.isOwner}
                          className="vote-btn-circle"
                        />
                      </Tooltip>
                      <div style={voteBoxStyle}>{voteIndex}</div>
                      <Tooltip title={comment.isOwner ? 'You cannot vote for your own comment' : ''}>
                        <Button
                          type="text"
                          icon={<ArrowDownOutlined style={{color: comment.isOwner ? '#bbb' : (comment.userVote==='down' ? '#f5222d' : '#222'), fontSize: 15}} />}
                          onClick={() => handleVote(comment.id, 'down')}
                          disabled={comment.isOwner}
                          className="vote-btn-circle"
                        />
                      </Tooltip>
                    </div>
                    {/* Main content */}
                    <div style={{flex:1}}>
                      <div style={{display:'flex',alignItems:'center',marginBottom:4,justifyContent:'space-between'}}>
                        <div style={{display:'flex',alignItems:'center'}}>
                          <div className="comment-avatar" style={{width:40,height:40,fontSize:20,background:'#8b5cf6',marginRight:12}}>{comment.userName[0]}</div>
                          <span style={{
                            fontWeight: 700,
                            fontSize: 16,
                            marginRight: 4,
                            color: comment.isMentor ? '#52c41a' : undefined
                          }}>{comment.userName}</span>
                          {comment.isMentor && (
                            <span style={{ 
                              fontSize: 11, 
                              color: '#52c41a', 
                              fontWeight: 500, 
                              background: '#eaffea', 
                              borderRadius: 4, 
                              padding: '2px 8px',
                              border: '1px solid #b7eb8f',
                              marginRight:4
                              
                            }}>mentor</span>
                          )}
                          <span style={{color:'#888',fontSize:14}}>• {timeAgo(comment.createdAt)}</span>
                        </div>
                        {/* Block upvote/downvote + menu actions */}
                        <div style={{display:'flex',alignItems:'center',gap:18}}>
                          <span style={{color:'#34c759',fontWeight:600,display:'flex',alignItems:'center',fontSize:15}}>
                            <ArrowUpOutlined style={{color:'#34c759',fontSize:15,marginRight:2}} />{comment.upvotes}
                          </span>
                          <span style={{color:'#f5222d',fontWeight:600,display:'flex',alignItems:'center',fontSize:15,marginLeft:8}}>
                            <ArrowDownOutlined style={{color:'#f5222d',fontSize:15,marginRight:2}} />{comment.downvotes}
                          </span>
                        {/* Dropdown cho comment  */}
                        {comment.isOwner && (
                          <Dropdown
                            overlay={
                              <Menu>
                                <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleEditComment(comment)}>Edit</Menu.Item>
                                <Menu.Item key="delete" icon={<DeleteOutlined />} onClick={() => handleDeleteComment(comment)}>Delete</Menu.Item>
                              </Menu>
                            }
                            trigger={['click']}
                            placement="bottomRight"
                          >
                            <Button type="text" icon={<MoreOutlined />} style={{fontSize:20}} />
                          </Dropdown>
                        )}
                        </div>
                      </div>
                      {editingCommentId === comment.id ? (
                        <div style={{marginBottom:8}}>
                           {/* edit */}
                          <QuillUpload
                            value={editingContent}
                            onChange={val => setEditingContent(val)}
                            readOnly={posting || loading}
                            className="comment-quill-editor"
                            style={{height:100}}
                          />
                          {(() => {
                            const plainLength = getPlainTextLength(editingContent);
                            const isOverLimit = plainLength > MAX_LENGTH;
                            const canSave = !isOverLimit && (plainLength > 0 || hasImage(editingContent));
                            return (
                              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 2 }}>
                                <span style={{ color: isOverLimit ? 'red' : '#bbb', fontSize: 13, marginRight: 12 }}>{plainLength}/{MAX_LENGTH}</span>
                                <Button type="primary" size="small" onClick={() => handleSaveEditComment(comment)} style={{marginTop:4,marginRight:8}} disabled={!canSave}>Save</Button>
                                <Button size="small" onClick={handleCancelEditComment} style={{marginTop:4}}>Cancel</Button>
                              </div>
                            );
                          })()}
                        </div>
                      ) : (
                        <div className="comment-text" style={{fontSize:16,marginBottom:8}} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(comment.content, { ADD_TAGS: ["img"], ADD_ATTR: ["src", "alt", "style"] }) + '<style>.comment-text img{max-width:100%;max-height:240px;border-radius:8px;margin:8px 0;display:block;}</style>' }} />
                      )}
                      <div style={{display:'flex',alignItems:'center',gap:16}}>
                        <Button size="small" icon={<CommentOutlined />} onClick={() => handleReplyClick(comment.id)}>
                          Reply
                        </Button>
                        {comment.repliesCount > 0 && (
                          <Button size="small" type="link" onClick={() => handleToggleReplies(comment.id)} style={{padding:0}}>
                            {isRepliesOpen ? <UpOutlined /> : <DownOutlined />} {isRepliesOpen ? `Hide ${comment.repliesCount} ${comment.repliesCount === 1 ? 'reply' : 'replies'}` : `Show ${comment.repliesCount} ${comment.repliesCount === 1 ? 'reply' : 'replies'}`}
                          </Button>
                        )}
                      </div>
                      {/* Reply form */}
                      {replyingTo === comment.id && (
                        <div className="reply-form">
                          <QuillUpload
                            value={replyInputs[comment.id] || ''}
                            onChange={val => setReplyInputs(inputs => ({ ...inputs, [comment.id]: val }))}
                            placeholder="Write a thoughtful reply..."
                            readOnly={posting || loading}
                            className="comment-quill-editor"
                          />
                          {(() => {
                            const plainLength = getPlainTextLength(replyInputs[comment.id] || '');
                            const isOverLimit = plainLength > MAX_LENGTH;
                            const canReply = !posting && !loading && !isOverLimit && (plainLength > 0 || hasImage(replyInputs[comment.id] || ''));
                            return (
                              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 2 }}>
                                <span style={{ color: isOverLimit ? 'red' : '#bbb', fontSize: 13, marginRight: 12 }}>{plainLength}/{MAX_LENGTH}</span>
                                <Button type="primary" size="small" onClick={() => handlePostReply(comment.id)} style={{ marginTop: 10, marginRight: 8 }} loading={posting} disabled={!canReply}>
                                  Reply
                                </Button>
                                <Button size="small" onClick={() => setReplyingTo(null)} style={{ marginTop: 10 }}>
                                  Cancel
                                </Button>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      {/* Replies */}
                      {comment.replies && comment.replies.length > 0 && isRepliesOpen && (
                        <div className="comment-replies">
                          {comment.replies.map(reply => (
                            <div key={reply.id} className="comment-reply-item" style={{display:'flex',alignItems:'flex-start',marginBottom:12,position:'relative'}}>
                              <div className="comment-avatar reply" style={{width:32,height:32,fontSize:15,background:'#a5b4fc',marginRight:10}}>{reply.userName[0]}</div>
                              <div className="comment-content">
                                <div className="comment-header" style={{display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                                  <div style={{display:'flex',alignItems:'center'}}>
                                    <span className="comment-user" style={{
                                      color: reply.isMentor ? '#52c41a' : undefined,
                                      fontWeight: reply.isMentor ? 600 : undefined,
                                      marginRight: 4
                                    }}>{reply.userName}</span>
                                    {reply.isMentor && (
                                      <span style={{ 
                                        fontSize: 10, 
                                        color: '#52c41a', 
                                        fontWeight: 500, 
                                        background: '#eaffea', 
                                        borderRadius: 3, 
                                        padding: '1px 6px',
                                        border: '1px solid #b7eb8f',
                                        marginLeft: 4,
                                        marginRight:4
                                      }}>mentor</span>
                                    )}
                                    <span className="comment-time"> • {timeAgo(reply.createdAt)}</span>
                                  </div>
                                  {/* Dropdown cho reply  */}
                                  {reply.isOwner && (
                                    <Dropdown
                                      overlay={
                                        <Menu>
                                          <Menu.Item key="edit" icon={<EditOutlined />} onClick={() => handleEditReply(reply, comment)}>Edit</Menu.Item>
                                          <Menu.Item key="delete" icon={<DeleteOutlined />} onClick={() => handleDeleteReply(reply, comment)}>Delete</Menu.Item>
                                        </Menu>
                                      }
                                      trigger={['click']}
                                      placement="bottomRight"
                                    >
                                      <Button type="text" icon={<MoreOutlined />} style={{fontSize:18}} />
                                    </Dropdown>
                                  )}
                                </div>
                                {/* reply */}
                                {editingReplyId === reply.id ? (
                                  <div style={{marginBottom:8}}>
                                    <QuillUpload
                                      value={editingReplyContent}
                                      onChange={val => setEditingReplyContent(val)}
                                      readOnly={posting || loading}
                                      className="comment-quill-editor"
                                    />
                                    {(() => {
                                      const plainLength = getPlainTextLength(editingReplyContent);
                                      const isOverLimit = plainLength > MAX_LENGTH;
                                      const canSave = !isOverLimit && (plainLength > 0 || hasImage(editingReplyContent));
                                      return (
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginTop: 2 }}>
                                          <span style={{ color: isOverLimit ? 'red' : '#bbb', fontSize: 13, marginRight: 12 }}>{plainLength}/{MAX_LENGTH}</span>
                                          <Button type="primary" size="small" onClick={() => handleSaveEditReply(reply, comment)} style={{marginTop:4,marginRight:8}} disabled={!canSave}>Save</Button>
                                          <Button size="small" onClick={handleCancelEditReply} style={{marginTop:4}}>Cancel</Button>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                ) : (
                                  <div className="comment-text" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(reply.content, { ADD_TAGS: ["img"], ADD_ATTR: ["src", "alt", "style"] }) + '<style>.comment-text img{max-width:100%;max-height:240px;border-radius:8px;margin:8px 0;display:block;}</style>' }} />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {loadingMore && (
                <div style={{textAlign:'center',padding:16}}><Spin /></div>
              )}
              {isLazyLimit && hasNextPage && !loadingMore && (
                <div style={{ textAlign: 'center', margin: 16 }}>
                  <Button onClick={handleShowMore}>Show more</Button>
                </div>
              )}
            </>
          )}
      </div>
      {showScrollTop && (
        <Tooltip title="Scroll to top" style={{}}>
          <Button
            type="primary"
            shape="circle"
            icon={<UpOutlined />}
            size="large"
            style={{ position: 'fixed', bottom: 48, right: 48, zIndex: 1000, boxShadow: '0 2px 8px #888' }}
            onClick={handleScrollToTop}
          />
        </Tooltip>
      )}
    </div>
  );
} 